/**
 * Follow-ups Needed
 * 
 * Replaces "Revenue Risk Alert" with a focus-oriented, time-based view.
 * Neutral language, compact design, action-first.
 */

import { useMemo, useState } from 'react';
import { Lead } from '@/hooks/useLeads';
import { Deal } from '@/hooks/useDeals';
import { formatCurrency } from '@/hooks/useUnifiedMetrics';
import { Clock, Phone, MessageCircle, Calendar } from 'lucide-react';
import { differenceInDays, format } from 'date-fns';
import { cn } from '@/lib/utils';
import { getContactLabel } from '@/lib/timeLabels';
import { Button } from '@/components/ui/button';
import { buildWhatsAppLink, buildTelLink } from '@/core/decision-engine/eliteEngine';
import { ScheduleFollowUpModal } from './ScheduleFollowUpModal';

interface FollowUpsNeededProps {
  leads: Lead[];
  deals: Deal[];
}

interface FollowUpDeal {
  deal: Deal;
  lead: Lead;
  status: 'never_contacted' | 'no_recent_activity' | 'decreasing_momentum';
  statusLabel: string;
  daysAgo: number | null;
}

export function FollowUpsNeeded({ leads, deals }: FollowUpsNeededProps) {
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<{ id: string; name: string } | null>(null);

  const { followUpDeals } = useMemo(() => {
    const now = new Date();
    const staleThreshold = 7;
    const followUps: FollowUpDeal[] = [];

    deals
      .filter(d => d.stage !== 'won' && d.stage !== 'lost' && d.value && d.value > 0)
      .forEach(deal => {
        const lead = leads.find(l => l.id === deal.lead_id);
        if (!lead) return;
        if (lead.pipeline_stage === 'won' || lead.pipeline_stage === 'lost') return;

        const neverContacted = !lead.last_interaction_at;
        const contactLabel = getContactLabel({
          neverContacted,
          lastInteractionAt: lead.last_interaction_at,
        });
        
        // Never contacted
        if (neverContacted) {
          followUps.push({
            deal,
            lead,
            status: 'never_contacted',
            statusLabel: contactLabel, // "Never contacted"
            daysAgo: null,
          });
          return;
        }

        // Calculate days since contact for filtering
        const lastContact = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
        const daysSinceContact = lastContact ? differenceInDays(now, lastContact) : null;
        
        // Sanitize: don't show unrealistic values (>= 365 days)
        if (daysSinceContact !== null && daysSinceContact >= 365) {
          return; // Skip leads with unrealistic stale days
        }

        // No recent activity (7+ days)
        if (daysSinceContact !== null && daysSinceContact >= staleThreshold) {
          followUps.push({
            deal,
            lead,
            status: 'no_recent_activity',
            statusLabel: contactLabel, // "X days ago"
            daysAgo: daysSinceContact,
          });
          return;
        }

        // Decreasing momentum (4-7 days, or no follow-up scheduled for qualified+)
        const followUp = lead.next_action_at ? new Date(lead.next_action_at) : null;
        if ((daysSinceContact && daysSinceContact >= 4 && daysSinceContact < 7) ||
            (!followUp && (deal.stage === 'qualified' || deal.stage === 'proposal' || deal.stage === 'negotiation'))) {
          followUps.push({
            deal,
            lead,
            status: 'decreasing_momentum',
            statusLabel: 'Decreasing momentum',
            daysAgo: daysSinceContact,
          });
        }
      });

    // Sort by deal value (highest first)
    followUps.sort((a, b) => (b.deal.value || 0) - (a.deal.value || 0));

    return {
      followUpDeals: followUps.slice(0, 3), // Max 3
    };
  }, [leads, deals]);

  if (followUpDeals.length === 0) return null;

  return (
    <div className="rounded-lg border border-border/50 bg-background/40 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Follow-ups Needed</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Based on time & inactivity
          </p>
        </div>
      </div>

      {/* Follow-up items - compact */}
      <div className="space-y-2">
        {followUpDeals.map(({ deal, lead, statusLabel, daysAgo }) => {
          const whatsappLink = buildWhatsAppLink(lead.phone);
          const telLink = buildTelLink(lead.phone);
          
          return (
            <div
              key={deal.id}
              className="flex items-center gap-3 p-2.5 rounded-md bg-background/60 border border-border/40 hover:border-primary/30 transition-colors group"
            >
              {/* Lead info - compact */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-sm text-foreground truncate">
                    {lead.name}
                  </span>
                  <span className="text-xs font-semibold text-primary">
                    {formatCurrency(deal.value || 0)}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  <span>{statusLabel}</span>
                </div>
              </div>

              {/* Actions - compact */}
              <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                {telLink && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(telLink, '_blank')}
                    className="h-7 px-2.5 text-xs gap-1.5"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    Call
                  </Button>
                )}
                {whatsappLink && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(whatsappLink, '_blank')}
                    className="h-7 px-2.5 text-xs gap-1.5"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedLead({ id: lead.id, name: lead.name });
                    setScheduleModalOpen(true);
                  }}
                  className="h-7 px-2.5 text-xs gap-1.5"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  Schedule
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Schedule Modal */}
      {selectedLead && (
        <ScheduleFollowUpModal
          open={scheduleModalOpen}
          onOpenChange={setScheduleModalOpen}
          leadId={selectedLead.id}
          leadName={selectedLead.name}
        />
      )}
    </div>
  );
}

